import { useState, useRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { Package, Users, BarChart3, LogOut, Gem, Calculator, FileImage, Menu, X, ChevronDown, Table2, Factory, Sparkles, Globe, FileText } from 'lucide-react';
import { USER_ROLE_LABELS } from '@/utils/constants';

const navItems = [
  { path: '/tasks', label: 'Tasks', icon: <Package size={16} /> },
  { path: '/all-tasks', label: 'All Tasks', icon: <Table2 size={16} />, roles: ['admin', 'executor', 'customer'] },
  { path: '/cad-sheets', label: 'CAD Sheets', icon: <FileImage size={16} />, roles: ['admin', 'executor'] },
  { path: '/portal-import', label: 'SGI Import', icon: <Globe size={16} />, roles: ['admin', 'executor'] },
  { path: '/manufacturing', label: 'Manufacturing', icon: <Factory size={16} />, roles: ['admin'] },
  { path: '/quotation', label: 'Quotation', icon: <Calculator size={16} />, roles: ['admin'] },
  { path: '/pd-ai', label: 'PD AI', icon: <Sparkles size={16} />, roles: ['admin'] },
  { path: '/analytics', label: 'Analytics', icon: <BarChart3 size={16} />, roles: ['admin'] },
  { path: '/audit-log', label: 'Audit Log', icon: <FileText size={16} />, roles: ['admin'] },
  { path: '/users', label: 'Users', icon: <Users size={16} />, roles: ['admin'] },
];

const CollapsibleLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  const userRole = user?.role || 'executor';
  const roleLabel = USER_ROLE_LABELS[userRole] || userRole;

  const visibleItems = navItems.filter(item => {
    if (!item.roles) return true;
    return item.roles.includes(userRole);
  });

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-background" data-testid="app-layout">
      {/* Top Navigation Bar */}
      <nav className="sticky top-0 z-50 border-b border-border bg-[#0f172a] text-white" data-testid="top-navbar">
        <div className="flex items-center justify-between h-12 px-4">
          {/* Left: Logo + Nav */}
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/tasks')}>
              <Gem size={20} className="text-amber-400" />
              <span className="font-bold text-sm tracking-wide hidden sm:inline">PD Tracker</span>
            </div>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-1">
              {visibleItems.map(item => {
                const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
                return (
                  <button
                    key={item.path}
                    onClick={() => navigate(item.path)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      isActive
                        ? 'bg-white/15 text-white'
                        : 'text-white/60 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid={`nav-${item.path.replace('/', '')}`}
                  >
                    {item.icon}
                    {item.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right: User Info */}
          <div className="flex items-center gap-3">
            <div className="relative" ref={userMenuRef}>
              <button
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                className="flex items-center gap-2 text-xs text-white/80 hover:text-white transition-colors"
                data-testid="user-menu-trigger"
              >
                <div className="w-7 h-7 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center text-amber-300 text-[11px] font-bold">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="hidden sm:block text-left">
                  <div className="text-xs font-medium leading-tight">{user?.name}</div>
                  <div className="text-[10px] text-white/50">{roleLabel}</div>
                </div>
                <ChevronDown size={14} className="text-white/40" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-44 bg-card border border-border rounded-lg shadow-xl z-50 overflow-hidden">
                  <div className="px-3 py-2 border-b border-border">
                    <p className="text-xs font-medium text-foreground truncate">{user?.name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{user?.email}</p>
                    <span className="inline-block mt-1 px-1.5 py-0.5 text-[9px] rounded bg-primary/10 text-primary font-medium">{roleLabel}</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-xs text-destructive hover:bg-muted/50 transition-colors"
                    data-testid="logout-btn"
                  >
                    <LogOut size={14} /> Sign Out
                  </button>
                </div>
              )}
            </div>

            {/* Mobile hamburger */}
            <button
              className="md:hidden p-1.5 rounded-md hover:bg-white/10 transition-colors"
              onClick={() => setMenuOpen(!menuOpen)}
              data-testid="mobile-menu-btn"
            >
              {menuOpen ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>

        {/* Mobile Nav Dropdown */}
        {menuOpen && (
          <div className="md:hidden border-t border-white/10 px-4 py-2 space-y-1">
            {visibleItems.map(item => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => { navigate(item.path); setMenuOpen(false); }}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                    isActive ? 'bg-white/15 text-white' : 'text-white/60 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {item.icon}
                  {item.label}
                </button>
              );
            })}
          </div>
        )}
      </nav>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
};

export default CollapsibleLayout;
